/**
 * ORDER CONSTANTS
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Imagine the backend saves an order as "OUT_FOR_DELIVERY" but a React
 * component checks for "OUT FOR DELIVERY" (space instead of underscore).
 * Nothing crashes. No error appears. The order simply never shows as
 * out-for-delivery, and you lose an evening finding out why.
 *
 * Defining the value ONCE and importing it everywhere makes that entire
 * category of bug structurally impossible. Misspell an imported constant and
 * you get an immediate, obvious `undefined` instead of silent wrongness.
 */

/**
 * The lifecycle of an order.
 * Values are UPPER_SNAKE_CASE because that is what gets stored in MongoDB.
 */
export const ORDER_STATUS = Object.freeze({
  PENDING: 'PENDING', // Created, awaiting store confirmation
  CONFIRMED: 'CONFIRMED', // Store accepted the order
  PACKED: 'PACKED', // Items picked and bagged
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY', // Handed to a delivery partner
  DELIVERED: 'DELIVERED', // Completed successfully
  CANCELLED: 'CANCELLED', // Cancelled by customer or store
  FAILED: 'FAILED', // Delivery attempt failed
});

/**
 * Human-readable labels for the UI.
 * Kept separate from the values above so we can translate to Hindi/Kannada
 * later without touching a single database record.
 */
export const ORDER_STATUS_LABEL = Object.freeze({
  [ORDER_STATUS.PENDING]: 'Order Placed',
  [ORDER_STATUS.CONFIRMED]: 'Confirmed',
  [ORDER_STATUS.PACKED]: 'Packed',
  [ORDER_STATUS.OUT_FOR_DELIVERY]: 'Out for Delivery',
  [ORDER_STATUS.DELIVERED]: 'Delivered',
  [ORDER_STATUS.CANCELLED]: 'Cancelled',
  [ORDER_STATUS.FAILED]: 'Delivery Failed',
});

/**
 * THE STATE MACHINE — which status may follow which.
 *
 * This prevents an admin from accidentally moving a DELIVERED order back to
 * PENDING, or skipping straight from PENDING to DELIVERED without the order
 * ever being packed. The backend validates every status change against this
 * map. An empty array means the status is terminal — nothing follows it.
 */
export const ORDER_STATUS_TRANSITIONS = Object.freeze({
  [ORDER_STATUS.PENDING]: [ORDER_STATUS.CONFIRMED, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.CONFIRMED]: [ORDER_STATUS.PACKED, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.PACKED]: [ORDER_STATUS.OUT_FOR_DELIVERY, ORDER_STATUS.CANCELLED],
  [ORDER_STATUS.OUT_FOR_DELIVERY]: [ORDER_STATUS.DELIVERED, ORDER_STATUS.FAILED],
  [ORDER_STATUS.DELIVERED]: [],
  [ORDER_STATUS.CANCELLED]: [],
  [ORDER_STATUS.FAILED]: [ORDER_STATUS.OUT_FOR_DELIVERY, ORDER_STATUS.CANCELLED],
});

/** Payment lifecycle. Reserved — payments arrive after the MVP. */
export const PAYMENT_STATUS = Object.freeze({
  PENDING: 'PENDING',
  PAID: 'PAID',
  FAILED: 'FAILED',
  REFUNDED: 'REFUNDED',
});

/** How a customer pays. Only COD is active in the MVP. */
export const PAYMENT_METHOD = Object.freeze({
  COD: 'COD', // Cash on delivery
  UPI: 'UPI',
  CARD: 'CARD',
  WALLET: 'WALLET',
});

/**
 * Statuses a customer is still allowed to cancel from.
 * Once an order is out for delivery, cancelling is a support call, not a button.
 */
export const CANCELLABLE_STATUSES = Object.freeze([ORDER_STATUS.PENDING, ORDER_STATUS.CONFIRMED]);

/**
 * Checks whether a status change is legal.
 * @param {string} from - current status
 * @param {string} to - requested status
 * @returns {boolean}
 */
export function canTransitionOrderStatus(from, to) {
  const allowed = ORDER_STATUS_TRANSITIONS[from];
  return Array.isArray(allowed) && allowed.includes(to);
}
